/**
 * Google Ads conversion labels.
 *
 * These are CONSTANTS, not configuration, and that distinction is the whole
 * point of this file. A label is the second half of `AW-XXXX/LABEL`: it is
 * minted once when a conversion action is created in the Ads account, it never
 * changes, and it is identical in every environment because there is exactly
 * one Ads account behind every environment.
 *
 * They used to be environment variables, which was wrong twice over:
 *
 * 1. **They are not secrets.** `getEnv()` ships them to `window.ENV` and gtag
 *    puts them in a request URL, so every visitor already has them. Storing a
 *    public constant in a secret manager buys no protection, only ceremony.
 * 2. **A missing one fails silently.** An unset label meant `fireAdsConversion`
 *    bailed, nothing errored, nothing warned, and the only symptom was zero
 *    conversions in the Ads UI weeks later. A constant in the repo cannot be
 *    forgotten on a deploy.
 *
 * What legitimately varies per environment is whether to report conversions at
 * all, and `GOOGLE_ADS_ID` still owns that: unset in dev and preview, set in
 * production. `fireAdsConversion` bails without it, so nothing here fires
 * outside prod. That is one environment variable doing one honest job.
 *
 * Adding a label: create the conversion action with the matching script under
 * `scripts/ads/`, which prints the label, then paste it here. It is a code
 * change on purpose — only an engineer ever touches these, and a commit is a
 * better record of when a conversion action changed than a mutated secret.
 */

/**
 * `null` means the conversion action does not exist in the Ads account yet.
 * Spelled out rather than left absent so a reader can tell "not created" from
 * "someone deleted a line".
 */
export const ADS_CONVERSION_LABELS = {
	/** `Purchase` — a completed Stripe Checkout. */
	purchase: 'OAxbCNHCzgMQh4WG4QM',
	/** `Signup` — an account finished onboarding. */
	signup: '7qknCJHu6AIQh4WG4QM',
	/** `Report Requested` — an email handed over at the scan report gate. */
	lead: 'UTZHCOH1tNocEIeFhuED',
	/**
	 * `Service Enquiry` — the /get-started form, the $299/mo offer's only
	 * conversion point.
	 */
	enquiry: '7VtzCOCT6uMcEIeFhuED',
} as const satisfies Record<string, string | null>
